import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { TutorCategoryType } from '@brighterly/lib-core-types/tutor/enums'
import type { LessonTutorFilters } from './interfaces'

import { useListsStore } from '@shared/model'

import { useLessonRecurringStore } from './recurring/store'
import { useDisciplineStore } from '../store'
import { buildTutorFilterOptions, getDefaultTutorFilters } from './lesson-create'
import { isRecurringScheduleOnly } from './lesson-recurring'

type LessonTutorTextFilter = Exclude<keyof LessonTutorFilters, 'categories'>

interface LessonRecurringFiltersReturn {
  refGender: WritableComputedRef<string[]>
  refSpecialFeatures: WritableComputedRef<string[]>
  refAccent: WritableComputedRef<string[]>
  refCategories: WritableComputedRef<string[]>
  refGenderOptions: ComputedRef<AppSelectOption<string>[]>
  refSpecialFeatureOptions: ComputedRef<AppSelectOption<string>[]>
  refAccentOptions: ComputedRef<AppSelectOption<string>[]>
  refCategoryOptions: ComputedRef<AppSelectOption<string>[]>
  refIsDisabled: ComputedRef<boolean>
}

/**
 * The tutor filters beside the recurring schedule. There is no Apply: what the panel shows is what the tutor modal
 * reads when it opens, so editing them asks the API nothing. They start on the discipline's defaults, as the legacy
 * filters step did, and are locked for a schedule-only form, which picks no tutor.
 */
export const useLessonRecurringFilters = (): LessonRecurringFiltersReturn => {
  const store = useLessonRecurringStore()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()

  const getCurrent = (): LessonTutorFilters => store.getFilters ?? getDefaultTutorFilters(disciplineStore.getDiscipline?.code)

  const bindFilter = (key: LessonTutorTextFilter): WritableComputedRef<string[]> => computed<string[]>({
    get: () => getCurrent()[key],
    set: (value) => {
      store.setFilters({ ...getCurrent(), [key]: [...value] })
    },
  })

  const refCategories = computed<string[]>({
    get: () => getCurrent().categories,
    set: (value) => {
      store.setFilters({ ...getCurrent(), categories: [...value] as TutorCategoryType[] })
    },
  })

  return {
    refGender: bindFilter('gender'),
    refSpecialFeatures: bindFilter('special_features'),
    refAccent: bindFilter('accent'),
    refCategories,
    refGenderOptions: computed(() => buildTutorFilterOptions(listsStore.getLists?.tutor.gender)),
    refSpecialFeatureOptions: computed(() => buildTutorFilterOptions(listsStore.getLists?.tutor.special_feature)),
    refAccentOptions: computed(() => buildTutorFilterOptions(listsStore.getLists?.tutor.accent)),
    refCategoryOptions: computed(() => buildTutorFilterOptions(listsStore.getLists?.tutor.category)),
    refIsDisabled: computed(() =>
      store.isSubmitting || isRecurringScheduleOnly(store.getFinishType, store.getOccurrences, store.getLessons)),
  }
}
